import axios from "@ianmartorell/restyped-axios";
import { AxiosError } from "axios";

import { API_URL, GIT_COMMIT } from "@/common/utils/env";
import navigateToLogin from "@/common/utils/navigateToLogin";
import APIDef, { API_VERSION_HEADER } from "@reflag/shared/api";

// TODO: Cypress tests that imports this file (AttributeFilters.tsx)
// currently breaks because Cypress uses CRA's webpack template and files in "shared"
// is not allowed due to the ModuleScopePlugin
// Example of the error here: https://github.com/reflag/web/actions/runs/3144577835/jobs/5110767881
// Temp workaround is to define the const directly in here instead

const forcedRefreshForStorageKey = "reflag-forced-refresh-for";
let logRefreshError = true;

function shouldCheckVersion(status?: number): boolean {
  return (
    !!status &&
    ((status >= 200 && status < 300) || (status >= 400 && status < 500))
  );
}

function getHeaderValue(
  headers: Record<string, unknown> | undefined,
  headerName: string,
): string | undefined {
  if (!headers) return undefined;

  const value = headers[headerName] ?? headers[headerName.toLowerCase()];
  return typeof value === "string" ? value : undefined;
}

function maybeForceRefresh(
  status: number | undefined,
  headers: Record<string, unknown> | undefined,
): void {
  if (!shouldCheckVersion(status)) return;

  const backendVersion = getHeaderValue(headers, API_VERSION_HEADER);
  if (!backendVersion || !GIT_COMMIT) return;
  if (backendVersion === GIT_COMMIT) return;

  const forcedRefreshFor = sessionStorage.getItem(forcedRefreshForStorageKey);

  if (forcedRefreshFor === backendVersion) {
    if (logRefreshError) {
      logRefreshError = false;
      console.error(
        `New API version "${backendVersion}" detected, but previous refresh failed. Please refresh manually.`,
      );
    }
    return;
  }

  console.warn(
    `New API version "${backendVersion}" detected (old: "${GIT_COMMIT}"), forcing refresh.`,
  );

  sessionStorage.setItem(forcedRefreshForStorageKey, backendVersion);
  window.location.replace(window.location.href);
}

function api() {
  axios.defaults.withCredentials = true;

  const apiInstance = axios.create<APIDef>({
    baseURL: API_URL,
  });

  apiInstance.interceptors.response.use(
    (res) => {
      maybeForceRefresh(
        res.status,
        res.headers as Record<string, unknown> | undefined,
      );

      return res;
    },
    (err: AxiosError) => {
      // User is not authenticated
      // Go to login page with a redirect back to the current location
      if (err.response?.status === 401) {
        navigateToLogin();
      }

      throw err;
    },
  );

  return apiInstance;
}

export default api();
